import React, { useMemo, useRef, useEffect } from "react";
import { liteClient } from "algoliasearch/lite";
import { InstantSearch } from "react-instantsearch";
import PropTypes from "prop-types";
import axios from "axios";
import qs from "qs";
import { isEmpty } from "ramda";

import UserPresenter from "presenters/UserPresenter";
import { externalRoutes } from "routes";
import { useUsers, useSettings } from "hooks";
import { jjLogger } from "utils/logUtils";

const EMPTY_RESULT = {
  hits: [],
  nbHits: 0,
  nbPages: 0,
  page: 0,
  processingTimeMS: 0,
};

const injectEmptyResultForEmptyQuery = (response) => {
  return {
    ...response,
    results: [EMPTY_RESULT, ...response.results],
  };
};

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const createCacheKey = (requests) => {
  return JSON.stringify(requests);
};

const AlgoliaSearchWrapper = ({
  proxy,
  children,
  context,
  indexName,
  stalledSearchDelayTime,
  initialUiState,
  initialQuery,
}) => {
  const { currentUser, currentUserLoaded } = useUsers();
  const {
    settings: { algolia },
  } = useSettings();
  const indexNameWithFallback = indexName || algolia.indexName;
  const uiState = initialQuery ? { [indexNameWithFallback]: { query: initialQuery } } : initialUiState;
  const requestCache = useRef(new Map());
  const searchClient = useMemo(() => {
    const algoliaClient = liteClient(algolia.id, algolia.searchKey);
    return {
      ...algoliaClient,
      async search(requests) {
        if (!currentUserLoaded || requests.every(({ params }) => !params.query)) {
          return Promise.resolve({
            results: requests.map(() => EMPTY_RESULT),
          });
        }

        const requestsWithoutEmptyQueries = requests.filter(
          (request) => request.params?.query && !isEmpty(request.params?.query),
        );
        const hasEmptyQuery = requests.some((request) => !request.params?.query || isEmpty(request.params?.query));

        const processedRequests = requestsWithoutEmptyQueries.map((request) => {
          return {
            ...request,
            params: {
              page: 0,
              ...request.params,
              enablePersonalization: true,
              enableReRanking: true,
              ruleContexts: context,
            },
          };
        });

        const shouldUseDirectAlgolia = !proxy || UserPresenter.isPaid(currentUser);
        let response;

        if (shouldUseDirectAlgolia) {
          response = await algoliaClient.search(processedRequests);
        } else {
          const cacheKey = createCacheKey(processedRequests);
          const cachedResponse = requestCache.current.get(cacheKey);

          if (cachedResponse) {
            response = cachedResponse;
          } else {
            const axiosResponse = await axios.get(externalRoutes.algoliaSearchProxy(), {
              params: processedRequests,
              paramsSerializer: (parameters) =>
                qs.stringify(parameters, {
                  encode: false,
                }),
            });

            response = axiosResponse.data;

            requestCache.current.set(cacheKey, response);

            setTimeout(() => {
              requestCache.current.delete(cacheKey);
            }, CACHE_TTL);
          }
        }

        return hasEmptyQuery ? injectEmptyResultForEmptyQuery(response) : response;
      },
    };
  }, [currentUserLoaded, currentUser, algolia, context, indexNameWithFallback, proxy]);

  useEffect(() => {
    return () => {
      requestCache.current.clear();
    };
  }, []);

  useEffect(() => {
    if (algolia.id && algolia.searchKey) {
      try {
        window.aa("init", {
          appId: algolia.id,
          apiKey: algolia.searchKey,
          useCookie: true,
        });
      } catch (error) {
        jjLogger.logError(`Failed to initialize search-insights: ${error}`);
      }
    }
  }, [algolia.id, algolia.searchKey]);

  return (
    <InstantSearch
      searchClient={searchClient}
      insights
      stalledSearchDelay={stalledSearchDelayTime}
      indexName={indexNameWithFallback}
      initialUiState={uiState}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      {children}
    </InstantSearch>
  );
};

AlgoliaSearchWrapper.propTypes = {
  proxy: PropTypes.bool,
  children: PropTypes.node.isRequired,
  context: PropTypes.string,
  indexName: PropTypes.string,
  stalledSearchDelayTime: PropTypes.number,
  initialUiState: PropTypes.shape(),
  initialQuery: PropTypes.string,
};

AlgoliaSearchWrapper.defaultProps = {
  proxy: true,
  context: "",
  indexName: "",
  stalledSearchDelayTime: 1000,
};

export default AlgoliaSearchWrapper;
